import React, { useEffect, useCallback, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useClerk, SignedIn, SignedOut, ClerkLoading, ClerkLoaded } from "@clerk/clerk-react";
import { ThemeSelection } from "./pages/ThemeSelection";
import { DifficultySelection } from "./pages/DifficultySelection";
import { GamePage } from "./pages/GamePage";
import { TutorialPage } from "./pages/TutorialPage";
import { CaseArchive } from "./pages/CaseArchive";
import FeaturedCaseEntry from "./pages/FeaturedCaseEntry";
import { AuthPage } from "./pages/AuthPage";
import { Toaster } from "./components/ui/toaster";
import { useToast } from "./hooks/use-toast";
import { useAuthSetup } from "./hooks/useAuthSetup";
import { useInactivityTimeout } from "./hooks/useInactivityTimeout";
import { InactivityWarningModal } from "./components/InactivityWarningModal";

// Lazy: the 3D view pulls in three.js + Spark, which nothing else needs.
// Keeping it out of the main chunk leaves /game's payload unchanged.
const Game3DPage = React.lazy(() => import("./pages/Game3DPage"));

// Protected route wrapper - requires authentication
// Uses SignedIn/SignedOut components which handle token refresh internally
// without unmounting children (unlike useAuth hook which can flicker)
// ClerkLoading/ClerkLoaded prevent blank screen during Clerk initialization
const ProtectedRoute = ({ children }) => (
  <>
    <ClerkLoading>
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    </ClerkLoading>
    <ClerkLoaded>
      <SignedIn>{children}</SignedIn>
      <SignedOut><Navigate to="/sign-in" replace /></SignedOut>
    </ClerkLoaded>
  </>
);

// Component to handle auth session expiry events
const AuthErrorHandler = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signOut } = useClerk();

  const handleAuthError = useCallback(async (event) => {
    const { message } = event.detail || {};
    console.warn('Auth session expired, signing out:', message);

    toast({
      title: "Session Expired",
      description: message || "Your session has expired. Please sign in again.",
      variant: "destructive",
      duration: 10000,
    });

    // Sign out from Clerk to clear the stale session state
    // This ensures the user sees the sign-in form and gets a fresh token
    try {
      await signOut();
    } catch (error) {
      console.warn('Error during sign out:', error);
    }

    // Navigate to sign-in (signOut may handle this, but ensure it happens)
    navigate('/sign-in');
  }, [navigate, toast, signOut]);

  useEffect(() => {
    window.addEventListener('auth-session-expired', handleAuthError);
    return () => {
      window.removeEventListener('auth-session-expired', handleAuthError);
    };
  }, [handleAuthError]);

  return null;
};

function AppContent() {
  // Set up auth token injection for API calls
  useAuthSetup();

  // Set up inactivity timeout (default: 2 hours)
  // Clerk's inactivity timeout is a premium feature, so this is our primary defense
  const { showWarning, remainingSeconds, dismissWarning } = useInactivityTimeout();

  return (
    <>
      <AuthErrorHandler />
      <InactivityWarningModal
        open={showWarning}
        onStayLoggedIn={dismissWarning}
        remainingSeconds={remainingSeconds}
      />
      <Routes>
        {/* Auth route - sign-in only (no self-registration) */}
        <Route path="/sign-in/*" element={<AuthPage />} />
        <Route path="/sign-up/*" element={<Navigate to="/sign-in" replace />} />

        {/* Protected routes - require login */}
        <Route path="/" element={<ProtectedRoute><ThemeSelection /></ProtectedRoute>} />
        <Route path="/tutorial" element={<ProtectedRoute><TutorialPage /></ProtectedRoute>} />
        <Route path="/difficulty" element={<ProtectedRoute><DifficultySelection /></ProtectedRoute>} />
        <Route path="/game" element={<ProtectedRoute><GamePage /></ProtectedRoute>} />
        {/* 3D view: the same session rendered as a HUD over a walkable 3D room.
            Featured cases with scenes open here on capable desktops. */}
        <Route
          path="/game3d"
          element={(
            <ProtectedRoute>
              <Suspense
                fallback={(
                  <div className="flex items-center justify-center min-h-screen bg-slate-950">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
                  </div>
                )}
              >
                <Game3DPage />
              </Suspense>
            </ProtectedRoute>
          )}
        />
        {/* Featured cases open in the real game screen; the entry only loads
            and hands off to /game or /game3d. There is no separate featured UI. */}
        <Route path="/featured/:slug" element={<ProtectedRoute><FeaturedCaseEntry /></ProtectedRoute>} />
        <Route path="/archive" element={<ProtectedRoute><CaseArchive /></ProtectedRoute>} />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
